import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { PasswordStrength } from "./PasswordStrength";
import { passwordRule } from "./passwordRules";
import { useChangePassword, useLogout, useMe } from "@/api/hooks/auth";
import { errorMessage, isApiError } from "@/api/errors";
import { Field, FormError } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ApiTokensCard } from "./ApiTokensCard";

const schema = z.object({
  current: z.string().min(1, "auth.currentRequired"),
  next: passwordRule,
});
type Values = z.infer<typeof schema>;

export function AccountPage() {
  const { t } = useTranslation();
  const me = useMe();
  const logout = useLogout();
  const change = useChangePassword();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { current: "", next: "" } });
  const { errors } = form.formState;

  const onSubmit = form.handleSubmit((v) =>
    change.mutate(
      { current_password: v.current, new_password: v.next },
      {
        onSuccess: () => {
          toast.success(t("auth.passwordChanged"));
          form.reset();
        },
      },
    ),
  );

  const e = change.error;
  const newPwServerError = isApiError(e) ? e.errors.new_password?.[0] : undefined;
  const formError = change.isError && !newPwServerError
    ? isApiError(e) && e.status === 400
      ? t("auth.currentIncorrect")
      : errorMessage(e, t("errors.generic"))
    : null;
  const newError = errors.next?.message ? t(errors.next.message) : (newPwServerError ?? null);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <PageHeader
        title={t("auth.accountTitle")}
        actions={
          <Button variant="secondary" onClick={() => logout.mutate()} disabled={logout.isPending}>
            {t("auth.signOut")}
          </Button>
        }
      />
      <Card>
        <CardHeader>
          <h2 className="text-xl">{t("auth.profileTitle")}</h2>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-label">
            <dt className="font-medium text-fg-muted">{t("auth.displayName")}</dt>
            <dd>{me.data?.name}</dd>
            <dt className="font-medium text-fg-muted">{t("auth.emailLabel")}</dt>
            <dd className="break-all">{me.data?.email}</dd>
          </dl>
          <p className="mt-4 text-sm text-fg-muted">{t("auth.nameReadOnly")}</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <h2 className="text-xl">{t("auth.changePasswordTitle")}</h2>
        </CardHeader>
        <CardContent>
          <form noValidate onSubmit={onSubmit} className="flex max-w-md flex-col gap-4">
            <FormError message={formError} />
            <Field label={t("auth.currentPassword")} error={errors.current?.message ? t(errors.current.message) : null}>
              {(p) => <Input {...p} type="password" autoComplete="current-password" {...form.register("current")} />}
            </Field>
            <Field label={t("auth.newPassword")} hint={t("auth.passwordHint")} error={newError}>
              {(p) => <Input {...p} type="password" autoComplete="new-password" {...form.register("next")} />}
            </Field>
            <PasswordStrength value={form.watch("next")} />
            <div>
              <Button type="submit" disabled={change.isPending}>
                {t("auth.changePassword")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      {me.data?.is_staff ? <ApiTokensCard /> : null}
    </div>
  );
}
